import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import {
  apiClient,
  getActiveBatchTempByMachine,
  getProdByMachineAndRef,
  getRtOperationsCount,
} from "../api";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtDateYMD = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const fmtTimeHMS = (d) => {
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mi}:${ss}`;
};
const toYMD = (v) => {
  if (!v) return "";
  const s = String(v);
  if (s.includes("T")) return s.slice(0, 10);
  if (v instanceof Date) return fmtDateYMD(v);
  return s.slice(0, 10);
};

// ── single machine row ────────────────────────────────────────────────────────
const MachineRow = ({ id_c, machine, refreshTick, now }) => {
  const cod_maq = machine.cod_maq;

  const [activeBatch, setActiveBatch] = useState(null);
  const [batchReason, setBatchReason] = useState(null);
  const [productInfo, setProductInfo] = useState(null);
  const [rtOps, setRtOps] = useState(0);

  // active batch
  useEffect(() => {
    if (!id_c || !cod_maq) return;
    getActiveBatchTempByMachine({ id_c, cod_maq })
      .then((res) => {
        setActiveBatch(res?.ok && res?.batch ? res.batch : res?.batch || null);
        setBatchReason(res?.reason || (res?.ok ? "single_active_batch" : "no_active_batch"));
      })
      .catch(() => { setActiveBatch(null); setBatchReason("error"); });
  }, [id_c, cod_maq, refreshTick]);

  // product info
  useEffect(() => {
    if (!activeBatch?.id_prod || batchReason === "multiple_active_batches") {
      setProductInfo(null);
      return;
    }
    getProdByMachineAndRef({ id_c, cod_maq, id_prod: activeBatch.id_prod })
      .then((res) => setProductInfo(res?.ok ? res.product : null))
      .catch(() => setProductInfo(null));
  }, [id_c, cod_maq, activeBatch?.id_prod, batchReason, refreshTick]);

  // RT operations
  useEffect(() => {
    if (!activeBatch?.date_begin || !activeBatch?.time_begin ||
        batchReason === "multiple_active_batches" || batchReason === "no_active_batch") {
      setRtOps(0);
      return;
    }
    getRtOperationsCount({
      id_c, cod_maq,
      start_date: toYMD(activeBatch.date_begin),
      start_time: String(activeBatch.time_begin).slice(0, 8),
      end_date: fmtDateYMD(new Date()),
      end_time: fmtTimeHMS(new Date()),
    })
      .then((res) => setRtOps(Number(res?.total_act || 0)))
      .catch(() => setRtOps(0));
  }, [id_c, cod_maq, activeBatch?.date_begin, activeBatch?.time_begin, batchReason, refreshTick]);

  // derived values
  const hoursUsed = useMemo(() => {
    if (!activeBatch || batchReason === "no_active_batch" ||
        batchReason === "multiple_active_batches") return 0;
    const startDateYMD = toYMD(activeBatch.date_begin);
    const startTime = String(activeBatch.time_begin || "").slice(0, 8);
    if (!startDateYMD || !startTime) return 0;
    let start = new Date(`${startDateYMD}T${startTime}`);
    let diffMs = now.getTime() - start.getTime();
    if (diffMs < 0) {
      const tmp = new Date(start);
      tmp.setDate(tmp.getDate() - 1);
      diffMs = now.getTime() - tmp.getTime();
    }
    const h = diffMs / (1000 * 60 * 60);
    return Number.isFinite(h) && h > 0 ? Number(h.toFixed(2)) : 0;
  }, [activeBatch, batchReason, now]);

  const opsStdHrNum = useMemo(() => {
    const v = Number(productInfo?.ciclo_x_hr_prod_std);
    return Number.isFinite(v) && v > 0 ? v : null;
  }, [productInfo]);

  const realOpsPerHour = useMemo(() => {
    if (!activeBatch || hoursUsed <= 0) return 0;
    const v = rtOps / hoursUsed;
    return Number.isFinite(v) ? Number(v.toFixed(2)) : 0;
  }, [activeBatch, rtOps, hoursUsed]);

  const efficiencyPct = useMemo(() => {
    if (!activeBatch || batchReason === "no_active_batch" ||
        batchReason === "multiple_active_batches" || opsStdHrNum === null) return null;
    const v = (realOpsPerHour / opsStdHrNum) * 100;
    return Number.isFinite(v) ? Number(v.toFixed(1)) : null;
  }, [activeBatch, batchReason, realOpsPerHour, opsStdHrNum]);

  const hasActive = activeBatch &&
    batchReason !== "no_active_batch" &&
    batchReason !== "multiple_active_batches";

  const opsColor = !hasActive || opsStdHrNum === null ? "#94a3b8"
    : realOpsPerHour >= opsStdHrNum ? "#22c55e" : "#ef4444";

  const effColor = efficiencyPct === null ? "#94a3b8"
    : efficiencyPct >= 100 ? "#22c55e" : "#ef4444";

  const COL_LABEL = { color: "#94a3b8", fontSize: 10, textTransform: "uppercase", marginBottom: 2 };
  const COL_VAL   = { color: "#e2e8f0", fontSize: 16, fontWeight: 600 };

  return (
    <div style={{
      display: "flex", alignItems: "center", flexWrap: "nowrap",
      background: "rgb(38,38,38)", borderRadius: 10,
      padding: "10px 16px", marginBottom: 8, gap: 8,
    }}>

      {/* Machine */}
      <div style={{ width: 140, flexShrink: 0 }}>
        <div style={COL_LABEL}>Máquina</div>
        <div style={{ ...COL_VAL, fontSize: 18, color: "#60a5fa" }}>{cod_maq}</div>
        <div style={{ color: "#94a3b8", fontSize: 11 }}>{machine.name_m || ""}</div>
      </div>

      {/* Lote */}
      <div style={{ width: 120, flexShrink: 0 }}>
        <div style={COL_LABEL}>Lote</div>
        <div style={COL_VAL}>
          {batchReason === "multiple_active_batches" ? "⚠️ Múltiples"
            : !hasActive ? "—" : activeBatch.id_batch}
        </div>
      </div>

      {/* Referencia + Producto */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={COL_LABEL}>Referencia / Producto</div>
        <div style={{ ...COL_VAL, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {!hasActive ? "Sin lote activo" : (activeBatch.id_prod || "—")}
        </div>
        <div style={{ color: "#94a3b8", fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {productInfo?.name_prod || ""}
        </div>
      </div>

      {/* Horas */}
      <div style={{ width: 75, textAlign: "center", flexShrink: 0 }}>
        <div style={COL_LABEL}>Horas</div>
        <div style={COL_VAL}>{!hasActive ? "—" : hoursUsed.toFixed(2)}</div>
      </div>

      {/* Operaciones */}
      <div style={{ width: 90, textAlign: "center", flexShrink: 0 }}>
        <div style={COL_LABEL}>Operaciones</div>
        <div style={COL_VAL}>{!hasActive ? "—" : rtOps}</div>
      </div>

      {/* Std / hr */}
      <div style={{ width: 75, textAlign: "center", flexShrink: 0 }}>
        <div style={COL_LABEL}>Std / Hr</div>
        <div style={COL_VAL}>{opsStdHrNum ?? "—"}</div>
      </div>

      {/* Real / hr — colored */}
      <div style={{
        width: 110, textAlign: "center", flexShrink: 0,
        background: "rgb(46,46,46)", borderRadius: 8, padding: "6px 4px",
      }}>
        <div style={COL_LABEL}>Operaciones Real / Hr</div>
        <div style={{ color: opsColor, fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>
          {!hasActive ? "—" : realOpsPerHour.toFixed(2)}
        </div>
      </div>

      {/* Eficiencia — colored */}
      <div style={{
        width: 110, textAlign: "center", flexShrink: 0,
        background: "rgb(46,46,46)", borderRadius: 8, padding: "6px 4px",
      }}>
        <div style={COL_LABEL}>Eficiencia</div>
        <div style={{ color: effColor, fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
          {efficiencyPct === null ? "—" : `${efficiencyPct}%`}
        </div>
      </div>
    </div>
  );
};

// ── main page ─────────────────────────────────────────────────────────────────
const ScreenTotal = () => {
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();

  const company = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("company")) || {}; } catch { return {}; }
  }, []);
  const id_c = company?.id_c ?? null;

  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setRefreshTick((v) => v + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const [allMachines, setAllMachines] = useState([]);
  useEffect(() => {
    if (!id_c) return;
    apiClient.get(`/company/${id_c}/machines`)
      .then(({ data }) =>
        setAllMachines(data?.machines?.map((m) => ({ cod_maq: m.cod_maq, name_m: m.name_m })) || [])
      )
      .catch(console.error);
  }, [id_c]);

  const ddmmyyyy = useMemo(() => {
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${now.getFullYear()}`;
  }, [now]);

  const hhmmss = useMemo(() => {
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${hh}:${mi}:${ss}`;
  }, [now]);

  return (
    <div className="screen1-rt">
      <header className="rt-header">
        <div className="rt-header__left">
          <img className="rt-logo-client" src={company?.logo_c} alt="Logo" />
        </div>
        <div className="rt-header__date">{ddmmyyyy}</div>
        <div className="rt-header__time">{hhmmss}</div>
        <div style={{ flex: 1, textAlign: "center" }}>
          <span style={{ color: "#e2e8f0", fontSize: 18, fontWeight: 600 }}>
            {t("screen2")}
          </span>
        </div>
        <div className="rt-header__right" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none", border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.45)", borderRadius: 6,
              padding: "4px 10px", fontSize: 12, cursor: "pointer",
              transition: "opacity 0.2s, color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "rgba(255,255,255,0.45)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
          >
            {t("screen_back")}
          </button>
          <img
            className="rt-logo-tt"
            src="https://storage.googleapis.com/tt202501/images/techtalamo_logo2.jpg"
            alt="Techtalamo"
          />
        </div>
      </header>

      <div style={{ padding: "16px 12px" }}>
        {allMachines.length === 0 && (
          <p style={{ color: "#94a3b8", textAlign: "center" }}>{t("MList1")}</p>
        )}
        {allMachines.map((machine) => (
          <MachineRow
            key={machine.cod_maq}
            id_c={id_c}
            machine={machine}
            refreshTick={refreshTick}
            now={now}
          />
        ))}
      </div>
    </div>
  );
};

export default ScreenTotal;
